import React, { useEffect, useState } from "react";
import { ChevronDown, Star } from "lucide-react";

const MAKHANA_IMG =
  "https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const FN_LOGO =
  "https://customer-assets.emergentagent.com/job_premium-makhana-11/artifacts/4l1hs9ak_FN%20Logo%20%281%29.png";

const floatingDots = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  left: `${8 + i * 9}%`,
  top: `${15 + (i % 4) * 18}%`,
  duration: `${3 + (i % 3)}s`,
  delay: `${i * 0.4}s`,
  size: i % 3 === 0 ? "w-2 h-2" : "w-1 h-1",
}));

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 150);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const reveal = (delay = 0) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.9s ease ${delay}s, transform 0.9s ease ${delay}s`,
  });

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={MAKHANA_IMG}
          alt="Premium Makhana from Bihar"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="hero-overlay absolute inset-0" />
        {/* Gold shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-transparent" />
      </div>

      {/* Floating Gold Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingDots.map((dot) => (
          <div
            key={dot.id}
            className={`absolute ${dot.size} rounded-full bg-[#D4AF37]/50 animate-float`}
            style={{
              left: dot.left,
              top: dot.top,
              "--duration": dot.duration,
              "--delay": dot.delay,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-4 pt-20">
        {/* Logo */}
        <div className="flex justify-center mb-8" style={reveal(0)}>
          <img
            src={FN_LOGO}
            alt="New Foxnourish Private Limited"
            className="w-24 h-24 md:w-32 md:h-32 rounded-full animate-pulse-gold border-2 border-[#D4AF37]/30"
            data-testid="hero-logo"
          />
        </div>

        {/* Brand Badge */}
        <div className="mb-6 flex justify-center" style={reveal(0.1)}>
          <span className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[5px] px-5 py-2 rounded-full bg-[#D4AF37]/8 backdrop-blur-sm">
            <Star size={10} className="fill-[#D4AF37]" />
            Premium Bihar Makhana
            <Star size={10} className="fill-[#D4AF37]" />
          </span>
        </div>

        {/* Main Brand Name */}
        <h1
          className="text-6xl md:text-8xl lg:text-9xl font-bold text-[#FAFAFA] mb-3 leading-none tracking-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            ...reveal(0.2),
          }}
          data-testid="hero-heading"
        >
          Makhana<span className="text-shimmer">G</span>
        </h1>

        {/* Hindi Tagline */}
        <p
          className="text-xl md:text-2xl text-[#D4AF37] mb-2 font-medium"
          style={{
            fontFamily: "'Noto Sans Devanagari', sans-serif",
            ...reveal(0.35),
          }}
          data-testid="hero-hindi-tagline"
        >
          प्रकृति की देन। बिहार की शान।
        </p>

        {/* English Tagline */}
        <p
          className="text-lg md:text-xl text-[#C9A84C] mb-8 tracking-wide font-medium"
          style={reveal(0.45)}
          data-testid="hero-tagline"
        >
          Crafted by Nature. Perfected by Bihar.
        </p>

        {/* Description */}
        <p
          className="text-base md:text-lg text-[#A1A1AA] max-w-2xl mx-auto mb-12 leading-relaxed"
          style={reveal(0.55)}
          data-testid="hero-description"
        >
          Premium Grade Raw Makhana (Fox Nuts) sourced directly from Bihar&apos;s 
          sacred lotus ponds. Available in 4 Suta, 5 Suta &amp; 6 Suta grades for 
          wholesale distributors &amp; retail buyers across India.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          style={reveal(0.65)}
          data-testid="hero-cta-container"
        >
          <button
            onClick={() => scrollTo("#products")}
            className="bg-[#D4AF37] text-black px-10 py-4 rounded-full text-base font-bold hover:bg-[#F4E3B2] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.5)] hover:shadow-[0_0_60px_rgba(212,175,55,0.8)] hover:scale-105 min-w-[220px]"
            data-testid="hero-explore-btn"
          >
            Explore Premium Grades
          </button>
          <button
            onClick={() => scrollTo("#distributor")}
            className="border-2 border-[#D4AF37] text-[#D4AF37] px-10 py-4 rounded-full text-base font-semibold hover:bg-[#D4AF37] hover:text-black transition-all duration-300 hover:scale-105 min-w-[220px]"
            data-testid="hero-distributor-btn"
          >
            Become a Distributor
          </button>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-4" style={reveal(0.75)}>
          {["Direct from Bihar Farms", "3 Premium Grades", "Pan India Delivery", "GST Compliant"].map((badge) => (
            <div key={badge} className="flex items-center gap-2 text-[#525252] text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
              {badge}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce"
        data-testid="scroll-indicator"
      >
        <span className="text-[#525252] text-[10px] uppercase tracking-[4px]">Scroll</span>
        <ChevronDown className="text-[#D4AF37]" size={18} />
      </div>
    </section>
  );
};

export default HeroSection;
